import { CdkMenuModule } from "@angular/cdk/menu";
import { CommonModule } from "@angular/common";
import {
    afterNextRender,
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import {
    DocumentDto,
    DocumentService,
    GuardianDto,
    UserDto,
} from "@malirang/api-client";
import { Relationship } from "@malirang/common";
import { Icon, ToastService } from "@malirang/front-share";
import { merge } from "rxjs";

export type DocumentShareParentItemOutput = {
    parentId: string;
    documentIds: string[];
    cancelledDocumentIds: string[];
};

@Component({
    selector: "app-document-share-parent-item",
    templateUrl: "./document-share-parent-item.component.html",
    styleUrls: ["./document-share-parent-item.component.css"],
    imports: [CommonModule, Icon, CdkMenuModule],
})
export class DocumentShareParentItemComponent {
    readonly documentService = inject(DocumentService);
    readonly toastService = inject(ToastService);

    parent = input<UserDto>();
    refresh = output<void>();
    guardian = computed<GuardianDto>(() => this.parent()?.guardians[0]!);
    relationShip = computed(() => Relationship[this.guardian()?.relationship]);

    firstCounseling = input<DocumentDto>();
    firstCounseling$ = toObservable(this.firstCounseling);
    report = input<DocumentDto>();
    report$ = toObservable(this.report);
    endReport = input<DocumentDto>();
    endReport$ = toObservable(this.endReport);

    documents = computed<string[]>(() => {
        const docs: string[] = [];
        if (this.firstCounseling()) docs.push(this.firstCounseling()?.id!);
        if (this.report()) docs.push(this.report()?.id!);
        if (this.endReport()) docs.push(this.endReport()?.id!);
        return docs;
    });
    selectedDocuments = signal<string[]>([]);
    selectedDocuments$ = toObservable(this.selectedDocuments);

    output = output<DocumentShareParentItemOutput>();

    cancelledDocuments = computed<string[]>(
        () =>
            this.documents()?.filter(
                (id) => !this.selectedDocuments().includes(id!),
            ) || [],
    );

    constructor() {
        afterNextRender(() => {
            this.handleView();

            merge(
                this.firstCounseling$,
                this.report$,
                this.endReport$,
            ).subscribe(() => this.handleView());
        });

        effect(() => {
            this.output.emit({
                parentId: this.parent()?.id || "",
                documentIds: this.selectedDocuments(),
                cancelledDocumentIds: this.cancelledDocuments() || [],
            });
        });
    }

    toggleDocument(documentId: string) {
        const selectedDocs = this.selectedDocuments();

        if (selectedDocs.includes(documentId)) {
            this.selectedDocuments.set(
                selectedDocs.filter((id) => id !== documentId),
            );
        } else {
            this.selectedDocuments.set([...selectedDocs, documentId]);
        }
    }

    handleView() {
        const selectedDocs: string[] = [];

        if (
            this.firstCounseling()?.sharedUsers?.some(
                (s) => s === this.parent()?.id,
            )
        )
            selectedDocs.push(this.firstCounseling()?.id!);
        if (this.report()?.sharedUsers?.some((s) => s === this.parent()?.id))
            selectedDocs.push(this.report()?.id!);
        if (this.endReport()?.sharedUsers?.some((s) => s === this.parent()?.id))
            selectedDocs.push(this.endReport()?.id!);

        this.selectedDocuments.set(selectedDocs);
    }
}
